/**
 * Project Timeline Engine
 * Automatically determines project status based on deadline and completion
 */

class ProjectTimelineEngine {
  /**
   * Determine project status based on deadline and completion
   * @param {Object} project - Project entity
   * @returns {Object} Status determination
   */
  determineProjectStatus(project) {
    if (!project.Deadline) {
      return {
        status: "On Track",
        statusColor: "blue",
        message: "No deadline set",
        isOverdue: false,
        daysRemaining: null,
      };
    }

    const now = new Date();
    const deadline = new Date(project.Deadline);
    const isCompleted =
      project.IsCompleted === true || project.IsCompleted === "true";

    // Calculate days remaining (can be negative if overdue)
    const timeDiff = deadline - now;
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    // If completed, always Met regardless of deadline
    if (isCompleted) {
      return {
        status: "Completed",
        statusColor: "green",
        message: "Project completed",
        isOverdue: false,
        daysRemaining: 0,
        completedOnTime: deadline >= now,
      };
    }

    // Not completed - check deadline
    if (daysRemaining < 0) {
      // Overdue
      return {
        status: "Delayed",
        statusColor: "red",
        message: `Overdue by ${Math.abs(daysRemaining)} days`,
        isOverdue: true,
        daysRemaining,
        shouldGenerateAlert: true,
        alertSeverity: "Critical",
      };
    } else if (daysRemaining <= 60) {
      // Due within 60 days
      return {
        status: "At Risk",
        statusColor: "yellow",
        message: `Due in ${daysRemaining} day${daysRemaining === 1 ? "" : "s"}`,
        isOverdue: false,
        daysRemaining,
        shouldGenerateAlert: true,
        alertSeverity: "Warning",
      };
      // } else if (daysRemaining <= 60) {
      //   // Due within 60 days
      //   return {
      //     status: 'On Track',
      //     statusColor: 'blue',
      //     message: `${daysRemaining} days remaining`,
      //     isOverdue: false,
      //     daysRemaining,
      //     shouldGenerateAlert: false
      //   };
    } else {
      // More than 30 days
      return {
        status: "On Track",
        statusColor: "blue",
        message: `${daysRemaining} days remaining`,
        isOverdue: false,
        daysRemaining,
        shouldGenerateAlert: false,
      };
    }
  }

  /**
   * Enhance all entities with deadline-based status
   * @param {Array} entities - All entities
   * @returns {Array} Enhanced entities
   */
  enhanceEntitiesWithDeadlineStatus(entities) {
    return entities.map((entity) => {
      if (
        entity.EntityType === "Project" ||
        entity.EntityType === "Initiative"
      ) {
        const deadlineStatus = this.determineProjectStatus(entity);
        return {
          ...entity,
          deadlineStatus,
          // Override auto-calculated status for projects with deadlines
          autoStatus: deadlineStatus.status,
        };
      }
      return entity;
    });
  }

  /**
   * Get projects requiring alerts
   * @param {Array} entities - All entities
   * @returns {Array} Projects that need alerts
   */
  getProjectsRequiringAlerts(entities) {
    const projects = entities.filter(
      (e) =>
        (e.EntityType === "Project" || e.EntityType === "Initiative") &&
        e.Deadline,
    );

    return projects
      .map((project) => ({
        ...project,
        deadlineStatus: this.determineProjectStatus(project),
      }))
      .filter((project) => project.deadlineStatus.shouldGenerateAlert);
  }

  /**
   * Format deadline for display
   */
  formatDeadline(deadline) {
    if (!deadline) return "No deadline";

    const date = new Date(deadline);
    const options = { year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(status) {
    const classes = {
      Completed: "badge-completed",
      "On Track": "badge-on-track",
      "At Risk": "badge-at-risk",
      Delayed: "badge-delayed",
      Blocked: "badge-blocked",
    };
    return classes[status] || "badge-default";
  }
}

export default new ProjectTimelineEngine();
